<script lang="ts">
	import { getFlagValue } from '@woltair/feature-flags-fe/client'
	import { BROWSER } from 'esm-env'
	import { onMount } from 'svelte'
	import { get } from 'svelte/store'
	import { goto } from '$app/navigation'
	import { page } from '$app/stores'
	import { getUTMs, sendGTM } from '$lib/analytics'
	import { Icon } from '$lib/components'
	import Table from '$lib/components/table/Table.svelte'
	import Td from '$lib/components/table/Td.svelte'
	import Tr from '$lib/components/table/Tr.svelte'
	import { INSTALLATION_CATEGORY, LEAD_SOURCE, REGEX, STEP } from '$lib/consts'
	import DynamicPageWrapper from '$lib/modules/cms/DynamicPageWrapper/DynamicPageWrapper.svelte'
	import { CALL_ME, CONTACT, PRIVACY_POLICY, DOCUMENTS, PHOTOVOLTAICS, OFFER } from '$lib/routes'
	import { t } from '$lib/translations'
	import { Transition } from '$lib/ui-woltair/components'
	import { v } from '$lib/ui-woltair/form'
	import * as Components from '$lib/ui-woltair/form'
	import { returnPathName } from '$lib/utils'
	import dataset from './_data'
	import { PUBLIC_WOLTAIR_WEB_CODE } from '$env/static/public'

	export let debug = false
	export let callButton = { deviceType: INSTALLATION_CATEGORY.FOTOVOLTAIKA }
	export let sign = null
	export let cls = ''
	export let version = null
	export let actionURL = null
	let loading = false
	let displayContact = false
	let errorMessage = false

	export let data

	const woltAdvisorLoading = () => {
		loading = true
		window.scrollTo(0, 0)
		setTimeout(() => (displayContact = true), 10000)
	}

	const killWoltAdvisorLoading = (emsg = true) => {
		loading = false
		errorMessage = emsg
	}
	$: if (BROWSER) {
		const krok = $page.url.searchParams.get(STEP)
		if (krok == 0 || !krok) sendGTM('pv_calculator_enter')
		if (['5a', '5b'].includes(krok)) sendGTM('pv_calculator_contact')
	}

	// dataset['start'].components[0].attrs.references = data.references

	if (dataset['5a']?.buttons[1] && dataset['5b']?.buttons[1]) {
		dataset['5a'].buttons[1].attrs.disabled = false
		dataset['5b'].buttons[1].attrs.disabled = false
	}

	const FORM_PROPS = {
		WOLTAIR_CZ: {
			initialValues: {
				organizationId: $page.data.organization.id,
				leadSource: LEAD_SOURCE.WOLTADVISOR_PV,
				personalMeeting: false
			},
			storedInitialValues: {
				affiliateCode: '',
				variant: '',
				annualyElectroConsumption: null,
				waterHeatingWithHouseHeatSource: '',
				houseHeatType: '',
				airCondition: '',
				floorSquare: null,
				isoLevel: null,
				peopleCount: null,
				regionName: '',
				zipcode: '',
				email: '',
				phone: ''
			},
			validationSchema: {
				variant: v.string(),
				affiliateCode: v.string(),
				affiliateCodeShow: v.bool(),
				affiliateCodeValid: v.bool().when('affiliateCodeShow', {
					is: (value) => value === true,
					then: (schema) => schema.oneOf([true], t('forms.validations.affiliationCode.error')),
					otherwise: (schema) => schema.notRequired()
				}),
				annualyElectroConsumption: v.number().when('variant', {
					is: (val) => val === 'a',
					then: (schema) =>
						schema
							.typeError(t('web.woltadvisor.electroConsumption.validation.error'))
							.nullable()
							.positive(t('web.woltadvisor.electroConsumption.validation.positive'))
							.required(t('web.woltadvisor.electroConsumption.validation.annual')),
					otherwise: (schema) =>
						schema
							.typeError(t('web.woltadvisor.electroConsumption.validation.error'))
							.nullable()
							.positive(t('web.woltadvisor.electroConsumption.validation.positive'))
				}),
				waterHeatingWithHouseHeatSource: v.string(),
				houseHeatType: v.string(),
				airCondition: v.bool().nullable(),
				floorSquare: v
					.number()
					.nullable()
					.when('variant', {
						is: (val) => val === 'b',
						then: v
							.number()
							.typeError(t('web.woltadvisor.floorSquare.validation.typeError'))
							.nullable()
							.positive(t('web.woltadvisor.floorSquare.validation.positive'))
							.required(t('web.woltadvisor.floorSquare.validation.required'))
					}),
				isoLevel: v.string().nullable(),
				peopleCount: v.string().nullable(),
				personalMeeting: v.bool(),
				email: v
					.string()
					.email(t('forms.validations.email.format'))
					.required(t('forms.validations.email.required')),
				zipcode: v
					.string()
					.required('Zadejte prosím PSČ')
					.matches(REGEX.ZIP, 'PSČ není ve správném formátu'),
				phone: v
					.string()
					.matches(REGEX.PHONE, t('web.forms.phone.validate'))
					.required(t('web.forms.phone.required'))
			},
			submitFunction: async (values) => {
				try {
					woltAdvisorLoading()
					const resZip = await fetch(`${returnPathName($page.url.pathname)}/location`, {
						method: 'POST',
						body: JSON.stringify({
							zip: values.zipcode
						})
					})

					let regionFromZip = await resZip.json()
					values.regionName = regionFromZip.region
					const res = await fetch(`${returnPathName($page.url.pathname)}/action-cz`, {
						method: 'POST',
						body: JSON.stringify({
							values,
							utms: getUTMs($page.url.searchParams)
						}),
						headers: {
							accept: 'application/json'
						}
					})

					const { lead, error } = await res.json()
					if (lead && typeof lead === 'object') {
						sendGTM('offer_sent', { id: lead.id, item: 'FOTOVOLTAIKA_OFFER' })
						goto(`/${PHOTOVOLTAICS}/${OFFER}?leadUid=${lead.uid}`)
					} else {
						logger.error('Lead not created:', error?.response?.errors[0]?.code)
						killWoltAdvisorLoading(error?.response?.errors[0]?.code)
					}
				} catch (err) {
					logger.error('Error during the submit function:', err)
					killWoltAdvisorLoading()
				}
			}
		},
		WOLTAIR_DE: {
			initialValues: {
				organizationId: $page.data.organization.id,
				leadSource: LEAD_SOURCE.WOLTADVISOR_PV,
				personalMeeting: false
			},
			storedInitialValues: {
				variant: '',
				annualyElectroConsumption: null,
				waterHeatingWithHouseHeatSource: '',
				houseHeatType: '',
				airCondition: '',
				location: null,
				locationUnknown: false,
				floorSquare: null,
				isoLevel: null,
				peopleCount: null,
				regionName: '',
				email: '',
				phone: ''
			},
			validationSchema: {
				variant: v.string(),
				annualyElectroConsumption: v.number().when('variant', {
					is: (val) => val === 'a',
					then: (schema) =>
						schema
							.typeError(t('web.woltadvisor.electroConsumption.validation.error'))
							.nullable()
							.positive(t('web.woltadvisor.electroConsumption.validation.positive'))
							.required(t('web.woltadvisor.electroConsumption.validation.annual')),
					otherwise: (schema) =>
						schema
							.typeError(t('web.woltadvisor.electroConsumption.validation.error'))
							.nullable()
							.positive(t('web.woltadvisor.electroConsumption.validation.positive'))
				}),
				waterHeatingWithHouseHeatSource: v.string(),
				houseHeatType: v.string(),
				airCondition: v.bool().nullable(),
				floorSquare: v
					.number()
					.nullable()
					.when('variant', {
						is: (val) => val === 'b',
						then: v
							.number()
							.typeError(t('web.woltadvisor.floorSquare.validation.typeError'))
							.nullable()
							.positive(t('web.woltadvisor.floorSquare.validation.positive'))
							.max(400, t('web.woltadvisor.floorSquare.validation.max400'))
							.required(t('web.woltadvisor.floorSquare.validation.required'))
					}),
				isoLevel: v.string().nullable(),
				peopleCount: v.string().nullable(),
				location: '',
				locationUnknown: v.bool(),
				personalMeeting: v.bool(),
				email: '',
				phone: ''
			},
			submitFunction: async (values) => {
				try {
					woltAdvisorLoading()
					const res = await fetch(`${returnPathName($page.url.pathname)}/action-cz`, {
						method: 'POST',
						body: JSON.stringify({
							values,
							utms: getUTMs($page.url.searchParams)
						}),
						headers: {
							accept: 'application/json'
						}
					})

					const { lead, error } = await res.json()
					if (lead && typeof lead === 'object') {
						sendGTM('offer_sent', { id: lead.id, item: 'FOTOVOLTAIKA_OFFER' })
						goto(`/${PHOTOVOLTAICS}/${OFFER}?leadUid=${lead.uid}`)
					} else {
						logger.error('Lead not created:', error?.response?.errors[0]?.code)
						killWoltAdvisorLoading()
					}
				} catch (err) {
					logger.error('Error during the submit function:', err)
					killWoltAdvisorLoading()
				}
			}
		},
		WOLTAIR_PL: {
			initialValues: {
				organizationId: $page.data.organization.id,
				leadSource: LEAD_SOURCE.WOLTADVISOR_PV,
				conditions: false,
				personalMeeting: false
			},
			storedInitialValues: {
				annualyElectroConsumption: null,
				fveRoofType: null,
				location: null,
				email: '',
				phone: ''
			},
			validationSchema: {
				annualyElectroConsumption: v
					.number()
					.typeError('Roczne zużycie energii elektrycznej musi być liczbą')
					.nullable()
					.positive('Roczne zużycie energii elektrycznej musi być liczbą dodatnią')
					.required('Uzupełnij roczne zużycie energii'),
				fveRoofType: v.string(),
				location: '',
				personalMeeting: v.bool(),
				email: '',
				phone: '',
				conditions: v.checkbox('Najpierw zaakceptuj warunki'),
				marketingConsentEmail: v
					.bool()
					.oneOf([true], t('web.woltadvisor.conditions.validate'))
					.required(t('web.woltadvisor.conditions.validate')),
				marketingConsentPhone: v
					.bool()
					.oneOf([true], t('web.woltadvisor.conditions.validate'))
					.required(t('web.woltadvisor.conditions.validate')),
				marketingConsentPost: v
					.bool()
					.oneOf([true], t('web.woltadvisor.conditions.validate'))
					.required(t('web.woltadvisor.conditions.validate'))
			},
			submitFunction: async (values) => {
				try {
					woltAdvisorLoading()
					const res = await fetch(`${returnPathName($page.url.pathname)}/action-cz`, {
						method: 'POST',
						body: JSON.stringify({
							values,
							utms: getUTMs($page.url.searchParams)
						}),
						headers: {
							accept: 'application/json'
						}
					})

					const { lead, error } = await res.json()
					if (lead && typeof lead === 'object') {
						sendGTM('offer_sent', { id: lead.id, item: 'FOTOVOLTAIKA_OFFER' })
						goto(`/${PHOTOVOLTAICS}/${OFFER}?leadUid=${lead.uid}`)
					} else {
						logger.error('Lead not created:', error?.response?.errors[0]?.code)
						killWoltAdvisorLoading()
					}
				} catch (err) {
					logger.error('Error during the submit function:', err)
					killWoltAdvisorLoading()
				}
			}
		}
	}

	let formProps = FORM_PROPS[PUBLIC_WOLTAIR_WEB_CODE]
	let steps = PUBLIC_WOLTAIR_WEB_CODE === 'WOLTAIR_PL' ? '3' : '5'
	let enabledAB = PUBLIC_WOLTAIR_WEB_CODE === 'WOLTAIR_CZ'

	let testKey = 'a-b-sx-3213-conversion-optimization-on-contact-info-step-in-pv-calculator-i'
	let testvalue
	let collapsedElements

	const handleCall = (deviceType) => {
		$page.url.searchParams.delete(STEP)
		$page.url.searchParams.set('device', deviceType)
		goto(`/${CALL_ME}${$page.url.search}`)
	}

	const switchSubmitButton = (value) => {
		if (dataset['5a']?.buttons[1] && dataset['5b']?.buttons[1]) {
			dataset['5a'].buttons[1].attrs.disabled = value
			dataset['5b'].buttons[1].attrs.disabled = value
		}
	}

	async function getTest(testKey: string) {
		return getFlagValue(testKey, (val) => val)
	}

	onMount(async () => {
		if (enabledAB) {
			testvalue = await getTest(testKey)
			sendGTM('set_ab_test', {}, { ab_test: testKey + ':' + testvalue })
			replaceValues()
		}
	})

	const replaceValues = () => {
		if ([1, 2].includes(testvalue)) {
			collapsedElements = true
			dataset['5a'] = {
				components: [
					{
						this: 'Element',
						collapse: false,
						attrs: {
							tag: 'h3',
							margin: 'mt-3',
							content: 'Vaše kalkulace je připravena!'
						}
					},
					{
						this: 'Field',
						collapse: true,
						attrs: {
							name: 'email',
							label: 'Váš e-mail',
							testId: 'email',
							hint: 'Proč potřebujeme email?',
							hintTooltip:
								'Výsledek kalkulace vám zobrazíme a pošleme na tento email, ať ho máte vždy u sebe. Žádné další emaily vám posílat nebudeme.'
						}
					},
					{
						this: 'Field',
						collapse: true,
						attrs: {
							name: 'phone',
							label: 'Váš telefon',
							testId: 'phone',
							hint: 'Proč potřebujeme telefon?',
							hintTooltip:
								'<p class="mb-4">Náš expert se s vámi jednou spojí a můžete s ním probrat své otázky ke kalkulaci a jejím parametrům, případně vyřešit nejasnosti.</p><p>V případě zájmu si můžete domluvit zaměření technikem u vás. Pokud nebudete chtít, víckrát vám volat nebudeme, nemusíte se bát.</p>'
						}
					},
					{
						this: 'Affiliate',
						discreet: true,
						attrs: {
							tiny: true,
							flex: 'justify-center',
							name: 'affiliateCode',
							label: 'Zadat promo kód'
						}
					}
				],
				buttons: [
					{
						this: 'Button',
						attrs: {
							type: 'submit',
							center: false,
							margin: '!my-0',
							text: 'Zobrazit výsledek kalkulačky',
							testId: 'submit'
						}
					}
				],
				conditions: true
			}
			dataset['5b'] = dataset['5a']
		}
	}

	$: {
		if (BROWSER) {
			const step = $page.url.searchParams.get(STEP)
			if (step === 'start') sendGTM('pv_calculator_enter')
			if (['5a', '5b'].includes(step)) sendGTM('pv_calculator_contact')
		}
	}
</script>

<svelte:window
	on:affiliateEvent={(event) => {
		switchSubmitButton(
			(event.affil.visible && !event.affil.isOkay) || (event.affil.visible && event.affil.checking)
		)
	}}
/>

{#if data.type === 'productPage' && data.components}
	<DynamicPageWrapper
		header={data.header}
		title={data.title}
		components={data.components}
		type={data.type}
	/>
{:else}
	<div class="pt-8">
		{#if loading}
			<div class="flex w-full justify-center">
				<div
					class="mx-4 mt-12 flex w-full max-w-3xl flex-col items-center justify-center rounded-xl bg-white px-6 pb-12 pt-6 shadow-xl"
				>
					<h3 class="!mt-4">{t('web.woltadvisor.loading')}</h3>
					<div class="mt-8 aspect-square max-h-14">
						<img
							class="max-h-full max-w-full animate-spin"
							src="/images/icons/circle/loading.svg"
							alt="loading icon"
						/>
					</div>

					{#if displayContact}
						<p class="mt-8 text-center">
							{@html t('web.woltadvisor.loading.error', {
								link: `${CONTACT}`,
								class: 'text-primary-500'
							})}
						</p>
					{/if}
				</div>
			</div>
			<div class="hidden"><slot /></div>
		{:else}
			<h1 class="unstyled h3 text-center">
				{t('web.meta.PRODUCTS.PHOTOVOLTAICS.KALKULACKA.meta.title')}
			</h1>
			{#if errorMessage}
				<div class="mx-auto -mb-4 mt-8 flex max-w-full items-center justify-center gap-4">
					<img src="/images/icons/circle/error.svg" class="h-8 w-8" alt="Error" />
					<div class="w-[380px] rounded bg-primary-500 p-2 text-center text-neutral-100">
						{#if errorMessage == 'CUSTOMER_PHONE_INVALID'}
							{t('web.forms.phone.validate')}
						{:else}
							{@html t('web.forms.error.submit', {
								link: `${CONTACT}`,
								class: 'text-neutral-100 underline'
							})}
						{/if}
					</div>
				</div>
			{/if}

			<div class="mx-auto flex-row-reverse items-stretch justify-center md:flex">
				<!-- eslint-disable-next-line @typescript-eslint/no-unused-vars -->
				<Components.Frame {version} {steps} let:step {cls}>
					{#if sign}
						<img src={sign.src} alt={sign.alt} />
					{/if}
					<Components.Form
						{...formProps}
						{actionURL}
						{step}
						{debug}
						let:step
						success={$$slots.success}
					>
						<Transition key={get(step)}>
							{#if get(step) && dataset[get(step)]}
								{@const { components = [], buttons = [] } = dataset[get(step)]}
								{#each components as component}
									{#if (testvalue !== 2 || !(dataset[get(step)]?.conditions === true)) && !(component.discreet === true)}
										<svelte:component this={Components[component.this]} {...component.attrs} />
									{:else if !component.collapse && !(dataset[get(step)]?.conditions === true)}
										<svelte:component this={Components[component.this]} {...component.attrs} />
									{/if}
								{/each}
								{#if testvalue == 1}
									{#each components as component}
										{#if component.discreet === true}
											<svelte:component this={Components[component.this]} {...component.attrs} />
										{/if}
									{/each}
								{/if}
								{#if testvalue == 2 && dataset[get(step)]?.conditions === true}
									<Table cls="w-full my-6" th_padding="px-4 pb-4" tdcls="text-left">
										<svelte:fragment slot="tbody">
											<Tr>
												<Td>Výkon:</Td>
												<Td cls="text-right">5 kWp</Td>
											</Tr>
											<Tr>
												<Td>Počet panelů</Td>
												<Td cls="text-right">10 ks</Td>
											</Tr>
											<Tr>
												<Td>Kapacita baterií</Td>
												<Td cls="text-right">10,65 kWh</Td>
											</Tr>
											<Tr>
												<Td>Předpokládaná výrova</Td>
												<Td cls="text-right">5 000 kWp</Td>
											</Tr>
											<Tr>
												<Td><strong>Celková cena s DPH:</strong></Td>
												<Td cls="text-right">
													<span class="blur-sm">IOR YNT Kč</span>
												</Td>
											</Tr>
											<Tr>
												<Td><strong>Dotace</strong></Td>
												<Td cls="text-right">
													<span class="blur-sm">ZIM INI Kč</span>
												</Td>
											</Tr>
											<Tr>
												<Td><strong>Cena po dotaci</strong></Td>
												<Td cls="text-right">
													<span class="blur-sm">DA KOT Kč</span>
												</Td>
											</Tr>
											<Tr>
												<Td><strong>Roční úspora</strong></Td>
												<Td cls="text-right">
													<span class="blur-sm">WI NUP Kč</span>
												</Td>
											</Tr>
										</svelte:fragment>
									</Table>
									{#if !collapsedElements}
										{#each components as component}
											{#if component.collapse}
												<svelte:component this={Components[component.this]} {...component.attrs} />
											{/if}
											{#if component.discreet === true}
												<svelte:component this={Components[component.this]} {...component.attrs} />
											{/if}
										{/each}
									{:else}
										<div class="mb-12">
											<Components.Button
												on:click={() => (collapsedElements = false)}
												primary
												noWrap
												ma
												iconPre={{ src: '/images/icons/icon-lock.svg' }}
											>
												Zobrazit hodnoty
											</Components.Button>
										</div>
									{/if}
								{/if}
								{#if dataset[get(step)]?.conditions === 'checkbox'}
									<Components.Field
										name="conditions"
										margin="mt-4"
										type="checkbox"
										testId="conditions"
										maxWidth="500px"
									>
										<p class="subtext">
											{t('general.woltairConditions', {
												privacyLink: `/${DOCUMENTS}/${PRIVACY_POLICY}`
											})}
										</p>
									</Components.Field>
								{/if}
								<div class="mt-4 flex justify-center gap-5">
									{#each buttons as button}
										{#if !(dataset[get(step)]?.conditions === true && collapsedElements && testvalue === 2)}
											<svelte:component this={Components[button.this]} {...button?.attrs}>
												{button?.slot || ''}
											</svelte:component>
										{/if}
									{/each}
								</div>
								{#if ($page.data.featureConfig.meeting.hvac || $page.data.featureConfig.meeting.pv) && callButton && !dataset[get(step)]?.conditions && get(step) !== 'start'}
									<div class="border-top mt-3 pt-3">
										<p>{t('woltadvisor.leaveUsNumber')}</p>
										<Components.Button
											on:click={() => handleCall(callButton.deviceType)}
											secondary
											noWrap
											icon={{ src: '/images/avatars/team.webp' }}
										>
											{t('woltadvisor.callMe')}
										</Components.Button>
									</div>
								{/if}
								{#if dataset[get(step)]?.conditions === 'address'}
									<p class="subtext mt-2">
										{t('general.woltairConditionsAddress', {
											privacyLink: `/${DOCUMENTS}/${PRIVACY_POLICY}`
										})}
									</p>
								{/if}
								{#if [1, 2].includes(testvalue) && dataset[get(step)]?.conditions === true}
									<div class="-mt-6 block rounded-xl bg-neutral-100 p-6 text-left">
										<p class="font-bold">Dozvíte se:</p>
										<ul class="unstyled">
											<li class="unstyled mt-4">
												<Icon width="w-4" height="h-4" src="/images/icons/tick-circle.svg">
													Jaký je vhodný výkon fotovoltaiky pro váš dům
												</Icon>
											</li>
											<li class="unstyled my-2">
												<Icon width="w-4" height="h-4" src="/images/icons/tick-circle.svg">
													Kolik ušetříte na elektřině v jednotlivých měsících roku
												</Icon>
											</li>
											<li class="unstyled my-2">
												<Icon width="w-4" height="h-4" src="/images/icons/tick-circle.svg">
													Za jak dlouho se vám vrátí investice do fotovoltaiky
												</Icon>
											</li>
											<li class="unstyled my-2">
												<Icon width="w-4" height="h-4" src="/images/icons/tick-circle.svg">
													2 varianty výkonu fotovoltaické elektrárny
												</Icon>
											</li>
											<li class="unstyled my-2">
												<Icon width="w-4" height="h-4" src="/images/icons/tick-circle.svg">
													Kompletní cena včetně realizace a materiálu
												</Icon>
											</li>
										</ul>
									</div>
								{/if}
								{#if dataset[get(step)]?.conditions === true}
									<p class="subtext mt-2">
										{t('general.woltairConditions', {
											privacyLink: `/${DOCUMENTS}/${PRIVACY_POLICY}`
										})}
									</p>
								{/if}
							{/if}
						</Transition>
						<svelte:fragment slot="success">
							<slot name="success" />
						</svelte:fragment>
					</Components.Form>
				</Components.Frame>
			</div>
			<style lang="scss">
				.border-top {
					border-top: 2px solid #cccccc;
				}
				.subtext a {
					@apply text-primary-500;
				}
			</style>
		{/if}
	</div>
{/if}
